import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { searchAPI } from '../../utils/api';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  ExclamationTriangleIcon,
  VideoCameraIcon,
  DocumentTextIcon,
  MapPinIcon,
} from '@heroicons/react/24/solid';
import clsx from 'clsx';

const TYPE_ICONS = {
  alerts: ExclamationTriangleIcon,
  cameras: VideoCameraIcon,
  incidents: DocumentTextIcon,
  zones: MapPinIcon,
};

const TYPE_COLORS = {
  alerts: 'text-red-500',
  cameras: 'text-blue-500',
  incidents: 'text-amber-500',
  zones: 'text-emerald-500',
};

const SEVERITY_COLORS = {
  CRITICAL: 'bg-red-100 text-red-700',
  HIGH: 'bg-orange-100 text-orange-700',
  MEDIUM: 'bg-amber-100 text-amber-700',
  LOW: 'bg-blue-100 text-blue-700',
};

export default function GlobalSearch() {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  const { data, isFetching } = useQuery({
    queryKey: ['global-search', query],
    queryFn: () => searchAPI.search({ q: query, limit: 10 }),
    enabled: query.length >= 2,
    staleTime: 30000,
    placeholderData: (prev) => prev,
  });

  const results = data?.data?.results || [];

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Keyboard shortcut: Ctrl+K or Cmd+K
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSelect = useCallback((result) => {
    setIsOpen(false);
    setQuery('');
    if (result.type === 'alerts') {
      navigate(`/alerts?highlight=${result.id}`);
    } else if (result.type === 'cameras') {
      navigate(`/cameras`);
    } else if (result.type === 'incidents') {
      navigate(`/incidents`);
    } else if (result.type === 'zones') {
      navigate(`/zones`);
    }
  }, [navigate]);

  // Build human-readable title and subtitle per result type
  const getResultMeta = (result) => {
    const s = result.source || {};
    switch (result.type) {
      case 'alerts':
        return {
          title: s.type || 'Alert',
          subtitle: [s.zoneName, s.cameraName, s.siteName].filter(Boolean).join(' · '),
          badge: s.severity,
        };
      case 'zones':
        return {
          title: s.name || 'Zone',
          subtitle: [s.code, s.siteName].filter(Boolean).join(' · '),
          badge: s.riskLevel ? `${s.riskLevel} risk` : null,
        };
      case 'cameras':
        return {
          title: s.name || 'Camera',
          subtitle: [s.location, s.zoneName, s.siteName].filter(Boolean).join(' · '),
          badge: s.status,
        };
      case 'incidents':
        return {
          title: s.title || 'Incident',
          subtitle: s.siteName || '',
          badge: s.severity,
        };
      default:
        return {
          title: s.title || s.name || s.type || 'Result',
          subtitle: s.siteName || '',
          badge: null,
        };
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" aria-hidden="true" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
          placeholder="Search alerts, zones, cameras… (Ctrl+K)"
          className="w-48 lg:w-72 pl-9 pr-8 py-1.5 text-sm rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          aria-label="Global search"
          role="combobox"
          aria-expanded={isOpen && results.length > 0}
          aria-haspopup="listbox"
        />
        {query && (
          <button
            onClick={() => { setQuery(''); setIsOpen(false); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-600"
            aria-label="Clear search"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Results dropdown */}
      {isOpen && query.length >= 2 && (
        <div
          className="absolute top-full mt-1 left-0 right-0 w-80 lg:w-[420px] bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden z-50"
          role="listbox"
        >
          {isFetching && results.length === 0 && (
            <div className="px-4 py-3 text-sm text-slate-500">Searching…</div>
          )}

          {!isFetching && results.length === 0 && (
            <div className="px-4 py-6 text-center">
              <MagnifyingGlassIcon className="w-6 h-6 mx-auto text-slate-300 mb-1" />
              <p className="text-sm text-slate-500">No results for <span className="font-medium">"{query}"</span></p>
              <p className="text-xs text-slate-400 mt-0.5">Try searching by zone name, camera, alert type, or site</p>
            </div>
          )}

          {results.length > 0 && (
            <>
              {results.map((result) => {
                const Icon = TYPE_ICONS[result.type] || DocumentTextIcon;
                const color = TYPE_COLORS[result.type] || 'text-slate-500';
                const { title, subtitle, badge } = getResultMeta(result);

                return (
                  <button
                    key={`${result.type}-${result.id}`}
                    onClick={() => handleSelect(result)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left border-b border-slate-50 last:border-0"
                    role="option"
                  >
                    <Icon className={clsx('w-4 h-4 flex-shrink-0', color)} aria-hidden="true" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{title}</p>
                      {subtitle && (
                        <p className="text-xs text-slate-500 truncate">{subtitle}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {badge && (
                        <span className={clsx(
                          'text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide',
                          SEVERITY_COLORS[badge] || 'bg-slate-100 text-slate-600'
                        )}>
                          {badge}
                        </span>
                      )}
                      <span className="text-[10px] font-medium uppercase tracking-wider text-slate-400">
                        {result.type}
                      </span>
                    </div>
                  </button>
                );
              })}

              <div className="px-4 py-2 border-t border-slate-100 dark:border-slate-800">
                <button
                  onClick={() => { navigate(`/alerts?search=${encodeURIComponent(query)}`); setIsOpen(false); }}
                  className="text-xs text-blue-500 hover:text-blue-600"
                >
                  View all alert results for "{query}"
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
